import { useEffect, useMemo, useState } from "react";

import { FirebaseAuth } from "../components/FirebaseAuth/firebase";
import { isContractorApproved } from "../libs/contractorReview";
import { buildMonthlySeries, formatMoney, formatNumber, monthKeyFromDate, toDate } from "../libs/dashboardOverview";

const readField = (row = {}, snakeKey, fallback = undefined) => {
  if (row && Object.prototype.hasOwnProperty.call(row, snakeKey)) return row[snakeKey];
  return fallback;
};

const normalizeInvoice = (id, data = {}) => ({
  id,
  ...data,
  invoiceId: readField(data, "invoice_id", id),
  timesheetId: readField(data, "timesheet_id", ""),
  userId: readField(data, "user_id", ""),
  hoursWorked: readField(data, "hours_worked", 0),
  createdAt: readField(data, "created_at", null),
  dueDate: readField(data, "due_date", ""),
  updatedAt: readField(data, "updated_at", null),
});

const normalizeTimesheet = (id, data = {}) => ({
  id,
  ...data,
  totalHours: readField(data, "total_hours", 0),
  totalCost: readField(data, "total_cost", 0),
  createdAt: readField(data, "created_at", null),
  updatedAt: readField(data, "updated_at", null),
});

const useContractorDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [accountExist, setAccountExist] = useState(false);
  const [token, setToken] = useState(undefined);
  const [approved, setApproved] = useState(false);
  const [role, setRole] = useState("contractor");
  const [invoices, setInvoices] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [invitations, setInvitations] = useState([]);

  useEffect(() => {
    let mounted = true;

    const getUserData = async () => {
      setLoading(true);
      const user = FirebaseAuth.auth().currentUser;
      if (!user) {
        if (mounted) {
          setAccountExist(false);
          setToken(undefined);
          setApproved(false);
          setRole("contractor");
          setLoading(false);
        }
        return;
      }

      const db = FirebaseAuth.firestore();
      const userDoc = await db.collection("users").doc(user.uid).get();
      const data = userDoc.data() || {};
      const invitationsFn = FirebaseAuth.functions().httpsCallable("getContractorHireInvitations");
      const [invoicesRes, timesheetsRes, invitationsRes] = await Promise.allSettled([
        db.collection("users").doc(user.uid).collection("invoices").get(),
        db.collection("users").doc(user.uid).collection("timesheets").get(),
        invitationsFn({}),
      ]);

      if (mounted) {
        setAccountExist(!!data.account_created);
        setToken(data);
        setApproved(isContractorApproved({ ...data, role: data.role || "contractor" }));
        setRole(data.role || "contractor");
        setInvoices(invoicesRes.status === "fulfilled" ? invoicesRes.value.docs.map((doc) => normalizeInvoice(doc.id, doc.data() || {})) : []);
        setTimesheets(timesheetsRes.status === "fulfilled" ? timesheetsRes.value.docs.map((doc) => normalizeTimesheet(doc.id, doc.data() || {})) : []);
        setInvitations(invitationsRes.status === "fulfilled" && Array.isArray(invitationsRes.value?.data?.contracts) ? invitationsRes.value.data.contracts : []);
        setLoading(false);
      }
    };

    getUserData();

    return () => {
      mounted = false;
    };
  }, []);

  const overview = useMemo(() => {
    const openInvitations = invitations.filter((row) => ["review", "shortlisted"].includes(String(row.status || "").toLowerCase()));
    const approvedTimesheets = timesheets.filter((row) => String(row.status || "").toLowerCase() === "approved");
    const rejectedTimesheets = timesheets.filter((row) => String(row.status || "").toLowerCase() === "rejected");
    const paidInvoices = invoices.filter((row) => String(row.status || "").toLowerCase() === "paid");
    const approvedInvoices = invoices.filter((row) => String(row.status || "").toLowerCase() === "approved");
    const openInvoices = invoices.filter((row) => !["paid", "approved"].includes(String(row.status || "").toLowerCase()));
    const totalPaid = paidInvoices.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const approvedUnpaid = approvedInvoices.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const openAmount = openInvoices.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const approvedHours = approvedTimesheets.reduce((sum, row) => sum + Number(row.totalHours || 0), 0);
    const submittedHours = timesheets
      .filter((row) => ["open", "submitted", "pending"].includes(String(row.status || "open").toLowerCase()))
      .reduce((sum, row) => sum + Number(row.totalHours || 0), 0);
    const seriesRows = invoices.map((row) => ({
      ...row,
      monthKey: String(row.month || "") || monthKeyFromDate(toDate(row.createdAt || row.updatedAt)),
    }));
    const buildEarningsSeries = (months) => buildMonthlySeries(
      seriesRows,
      (row) => row.monthKey,
      (row) => Number(row.amount || 0),
      months
    );

    return {
      summary: [
        { label: "Paid earnings", value: formatMoney(totalPaid), helper: `${paidInvoices.length} paid invoice${paidInvoices.length === 1 ? "" : "s"}` },
        { label: "Open invoices", value: formatMoney(openAmount + approvedUnpaid), helper: `${openInvoices.length + approvedInvoices.length} not paid` },
        { label: "Approved hours", value: formatNumber(approvedHours), helper: "Ready for billing" },
        { label: "Active engagements", value: invitations.filter((row) => ["matched", "signed", "active"].includes(String(row.status || "").toLowerCase())).length, helper: "Matched or active" },
      ],
      seriesByRange: {
        3: buildEarningsSeries(3),
        6: buildEarningsSeries(6),
        12: buildEarningsSeries(12),
      },
      breakdowns: {
        invoices: [
          { label: "Paid", value: totalPaid },
          { label: "Approved", value: approvedUnpaid },
          { label: "Open", value: openAmount },
        ],
        work: [
          { label: "Approved", value: approvedHours },
          { label: "Submitted", value: submittedHours },
          { label: "Rejected", value: rejectedTimesheets.reduce((sum, row) => sum + Number(row.totalHours || 0), 0) },
        ],
        opportunities: [
          { label: "Invited", value: openInvitations.length },
          { label: "Accepted", value: invitations.filter((row) => ["accepted", "matched", "signed", "active"].includes(String(row.status || "").toLowerCase())).length },
          { label: "Closed", value: invitations.filter((row) => ["rejected", "expired", "closed"].includes(String(row.status || "").toLowerCase())).length },
        ],
      },
      metrics: [],
      series: buildEarningsSeries(6),
    };
  }, [invitations, invoices, timesheets]);

  const navBadges = useMemo(() => ({
    opportunities: invitations.filter((row) => ["review", "shortlisted"].includes(String(row.status || "").toLowerCase())).length,
    work: timesheets.filter((row) => ["open", "submitted", "pending"].includes(String(row.status || "open").toLowerCase())).length,
    earnings: invoices.filter((row) => String(row.status || "").toLowerCase() === "paid").length,
    billing: invoices.filter((row) => String(row.status || "").toLowerCase() !== "paid").length,
  }), [invitations, invoices, timesheets]);

  return {
    accountExist,
    approved,
    loading,
    navBadges,
    overview,
    role,
    token,
  };
};

export default useContractorDashboard;
